import React from "react";
import { FastForward, Pause, Play, Rewind, SkipBack, SkipForward, X } from "lucide-react";
import { useRecordingPlayerActions, useRecordingPlayerState } from "./RecordingPlayerContext";
import ShareRecordingDialog from "./ShareRecordingDialog";

const formatTime = (secs) => {
  if (!Number.isFinite(secs) || secs < 0) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const ControlButton = ({ label, onClick, children, className = "" }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    className={`flex items-center justify-center h-9 min-w-[36px] flex-1 rounded-full text-white hover:bg-white/15 active:bg-white/25 ${className}`}
  >
    {children}
  </button>
);

// Notification-style player shown after a Sales Recording pop-out is closed while it plays.
// It sits in the page's flow at the top, like ImpersonationBanner, so it pushes the page
// down instead of covering it. `data-sticky-top` makes StickyBar toolbars park below it.
// The full-width wrapper ignores pointer events so the page options under it stay tappable;
// the panel turns them back on so the banner's own buttons work.
const RecordingBanner = () => {
  const player = { ...useRecordingPlayerState(), ...useRecordingPlayerActions() };
  if (!player.track) return null;

  const { track, playing, time, duration } = player;

  return (
    <div
      role="region"
      aria-label="Now playing"
      data-sticky-top=""
      className="sticky top-0 z-40 pointer-events-none flex justify-center px-2 pt-[calc(env(safe-area-inset-top)+0.5rem)] pb-2"
    >
      <div className="pointer-events-auto w-full max-w-[420px] rounded-2xl border border-slate-700 bg-slate-900/95 backdrop-blur shadow-2xl px-3 py-2 text-white">
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex-1 min-w-0 truncate text-sm font-semibold">{track.title}</span>
          <button
            type="button"
            aria-label="Close and stop"
            title="Close and stop"
            onClick={player.closeTrack}
            className="flex items-center justify-center h-8 w-8 flex-shrink-0 rounded-full hover:bg-white/15"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-2 mt-1">
          <span className="text-[11px] text-gray-400 w-9 text-right tabular-nums">{formatTime(time)}</span>
          <input
            type="range"
            aria-label="Seek"
            min={0}
            max={duration || 0}
            step={1}
            value={Math.min(time, duration || 0)}
            onChange={(e) => player.seekTo(Number(e.target.value))}
            className="flex-1 min-w-0 accent-emerald-500"
          />
          <span className="text-[11px] text-gray-400 w-9 tabular-nums">{formatTime(duration)}</span>
        </div>

        <div className="flex items-center gap-1 mt-1">
          <ControlButton label="Previous recording" onClick={player.previous}>
            <SkipBack className="h-4 w-4" />
          </ControlButton>
          <ControlButton label="Rewind 10 seconds" onClick={() => player.skipBy(-10)}>
            <Rewind className="h-4 w-4" />
          </ControlButton>
          <ControlButton label={playing ? "Pause" : "Play"} onClick={player.togglePlay} className="bg-white/10">
            {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
          </ControlButton>
          <ControlButton label="Forward 10 seconds" onClick={() => player.skipBy(10)}>
            <FastForward className="h-4 w-4" />
          </ControlButton>
          <ControlButton label="Next recording" onClick={player.next}>
            <SkipForward className="h-4 w-4" />
          </ControlButton>
          <ShareRecordingDialog
            recordingId={track.id}
            recordingLabel={track.title}
            trigger={
              <button
                type="button"
                className="flex items-center justify-center h-9 min-w-[36px] flex-1 rounded-full text-white hover:bg-white/15"
                aria-label="Share recording"
                title="Share recording"
              >
                <span className="text-xs font-semibold">Share</span>
              </button>
            }
          />
          <button
            type="button"
            onClick={player.requestOpen}
            className="flex items-center justify-center h-9 min-w-[44px] flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-xs font-semibold"
          >
            Open
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordingBanner;
